import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './RetroPage.css';

interface Project {
  id: string;
  title: string;
  year: number;
  description: string;
  image: string;
  techStack: string[];
  features: string[];
  url: string | null;
  isGame?: boolean;
}

const RetroPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [hitCount, setHitCount] = useState(0);
  const [clockTime, setClockTime] = useState('');
  const [showGuestbook, setShowGuestbook] = useState(false);
  const [guestbookEntries, setGuestbookEntries] = useState<{name: string, msg: string, date: string}[]>([
    { name: 'WebSurfer98', msg: 'Cool homepage!! :)', date: '11/14/2002' },
    { name: 'NetscapeFan', msg: 'Best viewed in 800x600!', date: '08/22/2001' },
    { name: 'CyberKid', msg: 'Awesome animated GIFs!! ★★★★★', date: '03/05/2003' },
  ]);
  const [guestName, setGuestName] = useState('');
  const [guestMsg, setGuestMsg] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Glen Rhodes\'s Homepage - Welcome!!! :: Netscape';
    const fetchProjects = async () => {
      try {
        const response = await axios.get<Project[]>('/api/projects');
        setProjects(response.data.sort((a, b) => b.year - a.year));
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();

    // Hit counter (random fun number, persisted in localStorage)
    const stored = localStorage.getItem('retroHitCount');
    let count = stored ? parseInt(stored) : 1337;
    count = count + 1;
    localStorage.setItem('retroHitCount', String(count));
    setHitCount(count);

    // Clock
    const updateClock = () => {
      const d = new Date();
      let h = d.getHours();
      const m = String(d.getMinutes()).padStart(2, '0');
      const ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      setClockTime(`${h}:${m} ${ampm}`);
    };
    updateClock();
    const clockInterval = setInterval(updateClock, 30000);
    return () => clearInterval(clockInterval);
  }, []);

  const padCounter = (n: number) => String(n).padStart(7, '0');

  const handleGuestbookSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!guestName.trim() || !guestMsg.trim()) return;
    const today = new Date();
    const dateStr = `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}/${today.getFullYear()}`;
    setGuestbookEntries([{ name: guestName, msg: guestMsg, date: dateStr }, ...guestbookEntries]);
    setGuestName('');
    setGuestMsg('');
  };

  if (loading) {
    return (
      <div className="retro-loading">
        <p>Loading... please wait... do not refresh!</p>
        <p style={{ fontSize: '11px' }}>(Connecting at 56k...)</p>
      </div>
    );
  }

  return (
    <div className="retro-page">
      {/* Browser chrome */}
      <div className="retro-browser-chrome">
        <div className="retro-titlebar">
          <span className="retro-titlebar-text">
            <img src="/images/myportrait.png" alt="N" className="retro-titlebar-icon" />
            Glen Rhodes's Homepage - Welcome!!! - Netscape
          </span>
          <div className="retro-titlebar-buttons">
            <button className="retro-tb-btn">_</button>
            <button className="retro-tb-btn">□</button>
            <button className="retro-tb-btn">×</button>
          </div>
        </div>

        <div className="retro-menubar">
          <span><u>F</u>ile</span>
          <span><u>E</u>dit</span>
          <span><u>V</u>iew</span>
          <span><u>G</u>o</span>
          <span><u>C</u>ommunicator</span>
          <span style={{ marginLeft: 'auto' }}><u>H</u>elp</span>
        </div>

        <div className="retro-toolbar">
          <button className="retro-toolbtn" onClick={() => navigate('/')} title="Back to Modern View">
            <div className="retro-toolicon">←</div>
            <div>Back</div>
          </button>
          <button className="retro-toolbtn" title="Forward">
            <div className="retro-toolicon">→</div>
            <div>Forward</div>
          </button>
          <button className="retro-toolbtn" title="Reload" onClick={() => window.location.reload()}>
            <div className="retro-toolicon">⟳</div>
            <div>Reload</div>
          </button>
          <button className="retro-toolbtn" title="Home" onClick={() => navigate('/retro')}>
            <div className="retro-toolicon">⌂</div>
            <div>Home</div>
          </button>
          <button className="retro-toolbtn" title="Search">
            <div className="retro-toolicon">🔍</div>
            <div>Search</div>
          </button>
          <button className="retro-toolbtn" title="Print">
            <div className="retro-toolicon">🖨</div>
            <div>Print</div>
          </button>
          <button className="retro-toolbtn" title="Stop">
            <div className="retro-toolicon">✋</div>
            <div>Stop</div>
          </button>
          <div className="retro-toolbar-spacer" />
          <div className="retro-n-logo">N</div>
        </div>

        <div className="retro-locationbar">
          <button className="retro-loc-btn">Bookmarks</button>
          <span className="retro-loc-label">Location:</span>
          <input
            className="retro-loc-input"
            value="http://www.glenrhodes.com/index.html"
            readOnly
          />
          <button className="retro-loc-btn">What's Related</button>
        </div>

        {/* Page content */}
        <div className="retro-content">
          {/* Logo / Header */}
          <div className="retro-header">
            <img
              src="/images/myportrait.png"
              alt="Glen"
              className="retro-portrait"
            />
            <h1 className="retro-logo-text">Glen Rhodes</h1>
          </div>
          <p className="retro-tagline">Your Friendly Full-Stack Web Developer & A.I. Engineer</p>

          <hr className="retro-hr" />

          {/* Marquee announcement */}
          <div className="retro-announce">
            <span className="retro-announce-icon">💾</span>
            {React.createElement('marquee', { scrollamount: 4 } as any,
              '★ Now with 56k modem support! ★ Y2K compliant! ★ Best viewed in Netscape Navigator 4.7+ ★ This page is under construction! ★ Sign my guestbook!! ★'
            )}
            <span className="retro-announce-icon">⚡</span>
          </div>

          <div className="retro-welcome-box">
            <p>
              <b>Welcome to my homepage!</b> Greetings cybernaut! You have stumbled upon the personal corner of the World Wide Web belonging to Glen Rhodes — full-stack developer, A.I. engineer, and digital creative person. Browse my projects below and don't forget to sign the guestbook before you log off!
            </p>
            <p style={{ marginTop: '8px' }}>
              <span className="retro-blink">NEW!</span>{' '}
              I have <b>{projects.length} totally rad projects</b> for you to check out. Click any of them to learn more!
            </p>
          </div>

          {/* Nav links */}
          <div className="retro-nav">
            <a href="#projects" onClick={(e) => { e.preventDefault(); document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' }); }}>My Projects</a>
            {' | '}
            <a href="#about" onClick={(e) => { e.preventDefault(); document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' }); }}>About Me</a>
            {' | '}
            <a href="#guestbook" onClick={(e) => { e.preventDefault(); setShowGuestbook(true); document.getElementById('guestbook')?.scrollIntoView({ behavior: 'smooth' }); }}>Guestbook</a>
            {' | '}
            <a href="mailto:grhodes@glenrhodes.com">Email Me</a>
            {' | '}
            <a href="https://twitter.com/glenrhodes" target="_blank" rel="noopener noreferrer">My Twitter</a>
            {' | '}
            <a href="https://www.linkedin.com/in/rhodesglen/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
          </div>

          <hr className="retro-hr" />

          {/* About */}
          <h2 id="about" className="retro-h2">📖 About Me</h2>
          <table className="retro-about-table">
            <tbody>
              <tr>
                <td className="retro-about-label">Name:</td>
                <td>Glen Rhodes</td>
              </tr>
              <tr>
                <td className="retro-about-label">Location:</td>
                <td>Mississauga, Ontario, Canada 🍁</td>
              </tr>
              <tr>
                <td className="retro-about-label">Occupation:</td>
                <td>Full-Stack Developer / A.I. Engineer</td>
              </tr>
              <tr>
                <td className="retro-about-label">Specialty:</td>
                <td>LLMs, agentic workflows, web apps, and shipping things that work</td>
              </tr>
              <tr>
                <td className="retro-about-label">Hobbies:</td>
                <td>Coding, music production, video games, writing</td>
              </tr>
              <tr>
                <td className="retro-about-label">Hire me:</td>
                <td><a href="mailto:grhodes@glenrhodes.com">grhodes@glenrhodes.com</a></td>
              </tr>
            </tbody>
          </table>

          <hr className="retro-hr" />

          {/* Projects */}
          <h2 id="projects" className="retro-h2">⭐ My Projects ⭐</h2>
          <p style={{ textAlign: 'center', marginBottom: '12px' }}>
            <i>(click any project to view more information)</i>
          </p>

          <table className="retro-projects-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Project Name</th>
                <th>Year</th>
                <th>Tech</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p, idx) => (
                <tr key={p.id} className={idx % 2 === 0 ? 'retro-row-alt' : ''}>
                  <td>{idx + 1}</td>
                  <td>
                    <a
                      href={`/project/${p.id}`}
                      onClick={(e) => { e.preventDefault(); navigate(`/project/${p.id}`); }}
                    >
                      <b>{p.title}</b>
                    </a>
                    <br />
                    <span className="retro-project-desc">{p.description.slice(0, 120)}{p.description.length > 120 ? '...' : ''}</span>
                  </td>
                  <td className="retro-cell-center">{p.year}</td>
                  <td className="retro-cell-tech">
                    {p.techStack.slice(0, 3).join(', ')}
                  </td>
                  <td className="retro-cell-center">
                    <a
                      href={`/project/${p.id}`}
                      onClick={(e) => { e.preventDefault(); navigate(`/project/${p.id}`); }}
                    >
                      [view]
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <hr className="retro-hr" />

          {/* Guestbook */}
          <h2 id="guestbook" className="retro-h2">📓 Guestbook</h2>
          <p style={{ textAlign: 'center' }}>
            <i>You are visitor number:</i>
          </p>
          <div className="retro-counter-wrap">
            <div className="retro-counter">{padCounter(hitCount)}</div>
          </div>
          <p style={{ textAlign: 'center', marginTop: '8px' }}>
            <button className="retro-button" onClick={() => setShowGuestbook(!showGuestbook)}>
              {showGuestbook ? 'Hide Guestbook' : 'Sign My Guestbook!'}
            </button>
          </p>

          {showGuestbook && (
            <div className="retro-guestbook">
              <form onSubmit={handleGuestbookSubmit} className="retro-guestbook-form">
                <table>
                  <tbody>
                    <tr>
                      <td><b>Your Name:</b></td>
                      <td>
                        <input
                          type="text"
                          value={guestName}
                          onChange={(e) => setGuestName(e.target.value)}
                          maxLength={30}
                          className="retro-input"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ verticalAlign: 'top' }}><b>Message:</b></td>
                      <td>
                        <textarea
                          value={guestMsg}
                          onChange={(e) => setGuestMsg(e.target.value)}
                          maxLength={200}
                          rows={3}
                          className="retro-textarea"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>
                        <button type="submit" className="retro-button">Submit Entry</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </form>

              <div className="retro-guestbook-entries">
                <h3 className="retro-h3">Recent Entries:</h3>
                {guestbookEntries.map((e, i) => (
                  <div key={i} className="retro-gb-entry">
                    <div><b>{e.name}</b> <span className="retro-gb-date">[{e.date}]</span></div>
                    <div className="retro-gb-msg">"{e.msg}"</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <hr className="retro-hr" />

          {/* Webrings / badges */}
          <div className="retro-badges">
            <div className="retro-badge">
              <div className="retro-badge-title">Best viewed in</div>
              <div className="retro-badge-name">Netscape<br />Navigator</div>
            </div>
            <div className="retro-badge retro-badge-yellow">
              <div className="retro-badge-title">NEW!</div>
              <div className="retro-badge-name">Java-powered<br />portfolio!</div>
            </div>
            <div className="retro-badge retro-badge-green">
              <div className="retro-badge-title">Y2K</div>
              <div className="retro-badge-name">COMPLIANT</div>
            </div>
            <div className="retro-badge retro-badge-blue">
              <div className="retro-badge-title">Powered by</div>
              <div className="retro-badge-name">A.I.<br />Technology</div>
            </div>
            <div className="retro-badge retro-badge-red">
              <div className="retro-badge-title">Made with</div>
              <div className="retro-badge-name">Notepad<br />♥</div>
            </div>
          </div>

          <hr className="retro-hr" />

          {/* Footer */}
          <div className="retro-footer">
            <p>© 1998-{new Date().getFullYear()} Glen Rhodes. All rights reserved.</p>
            <p>This page uses advanced <b>JavaScript</b> technology.</p>
            <p>Best viewed at 800x600 with Netscape Navigator 4.7 or higher.</p>
            <p style={{ marginTop: '8px' }}>
              <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                [Back to top]
              </a>
              {' | '}
              <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
                [Modern View]
              </a>
            </p>
            <p style={{ fontSize: '10px', marginTop: '8px', color: '#666' }}>
              Last updated: {new Date().toLocaleDateString()} | This page is part of the GeoCyber Webring
            </p>
          </div>
        </div>

        {/* Status bar */}
        <div className="retro-statusbar">
          <span className="retro-status-item">Document: Done</span>
          <span className="retro-status-item">🔒</span>
          <span className="retro-status-item">{clockTime}</span>
        </div>
      </div>
    </div>
  );
};

export default RetroPage;
